import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Hook: poll the support unread count for admins.
 *
 * Polls every 30 s, plus refetches whenever the tab regains focus and when
 * a child component dispatches the `support:read` window event (which
 * AdminSupportInbox fires after the admin marks something as in_progress
 * / resolved / spam — see usage there).
 *
 * Returns `{ unread, refresh }`. `refresh` lets callers force a re-poll
 * immediately after an action.
 */
export default function useSupportUnreadCount(enabled = true) {
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(() => {
    if (!enabled) return;
    api.get("/admin/support/unread-count")
      .then((r) => setUnread(r.data?.unread ?? 0))
      .catch(() => {});
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, 30_000);
    const onFocus = () => refresh();
    const onCustom = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("support:read", onCustom);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("support:read", onCustom);
    };
  }, [enabled, refresh]);

  return { unread, refresh };
}
